import React, { useState } from 'react';
import { X, QrCode, Copy, Check } from 'lucide-react';

interface QRCodeGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QRCodeGeneratorModal({ isOpen, onClose }: QRCodeGeneratorModalProps) {
  const [recipeId, setRecipeId] = useState('chestnut123');
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQRCodeUrl] = useState('');

  if (!isOpen) return null;

  const generateQRCodeUrl = () => {
    const appUrl = window.location.origin;
    const fullUrl = `${appUrl}/?qrcodescanned=${recipeId}`;

    // Use a QR code API to generate the image
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullUrl)}`;
    setQRCodeUrl(qrApiUrl);
  };

  const copyToClipboard = () => {
    const appUrl = window.location.origin;
    const fullUrl = `${appUrl}/?qrcodescanned=${recipeId}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const testDirectLink = () => {
    const appUrl = window.location.origin;
    window.location.href = `${appUrl}/?qrcodescanned=${recipeId}`;
  };

  const sampleRecipes = [
    { id: 'chestnut123', name: 'Chestnut Sourdough' },
    { id: 'rye456', name: 'Dark Rye Bread' },
    { id: 'ciabatta789', name: 'Italian Ciabatta' },
    { id: 'baguette101', name: 'French Baguette' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <QrCode className="w-6 h-6 text-amber-600" />
            <h2 className="text-xl font-semibold text-gray-900">QR Code Generator</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>For Testing:</strong> Generate QR codes to test the scanner functionality.
              These codes can be scanned with another device or tested directly.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipe ID
            </label>
            <input
              type="text"
              value={recipeId}
              onChange={(e) => {
                setRecipeId(e.target.value);
                setQRCodeUrl('');
              }}
              placeholder="Enter recipe ID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Select
            </label>
            <div className="grid grid-cols-2 gap-2">
              {sampleRecipes.map((recipe) => (
                <button
                  key={recipe.id}
                  onClick={() => {
                    setRecipeId(recipe.id);
                    setQRCodeUrl('');
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-left"
                >
                  {recipe.name}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={generateQRCodeUrl}
            className="w-full py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
          >
            Generate QR Code
          </button>

          {qrCodeUrl && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <img
                  src={qrCodeUrl}
                  alt="Generated QR Code"
                  className="w-full max-w-[300px] mx-auto"
                />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">URL:</p>
                <div className="bg-gray-50 p-3 rounded-lg break-all text-sm text-gray-600">
                  {window.location.origin}/?qrcodescanned={recipeId}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={copyToClipboard}
                  className="flex-1 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy URL</span>
                    </>
                  )}
                </button>
                <button
                  onClick={testDirectLink}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Test Direct Link
                </button>
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900 mb-2">
              <strong>How it works:</strong>
            </p>
            <ol className="text-sm text-amber-900 space-y-1 list-decimal list-inside">
              <li>Generate a QR code with a recipe ID</li>
              <li>Open the QR scanner in the app</li>
              <li>Scan the QR code with another device</li>
              <li>Or use "Test Direct Link" to simulate a scan</li>
            </ol>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-600">
              <strong>For Apple Review:</strong> You can use the "Test Direct Link" button
              to simulate scanning without needing a physical QR code. This demonstrates
              the functionality without requiring camera access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
